import { colors } from '../../../../theme/colors';
import { FMT } from '../../../../util/formatter-service';
import { IEquityLot, LifecycleLabels, LifecycleState } from './equity-holdings-model';

/**
 * Presentation logic for Security History. Kept out of the column defs so the thresholds and label
 * choices are testable without a grid.
 */

/**
 * MOIC is colour-scaled, using the prototype's bands: below break-even is a loss, up to 1.5x is thin,
 * up to 2.5x is healthy, above that is strong. The theme already carries the prototype's exact
 * palette, so these are tokens rather than hex values.
 */
export function getMoicColor(moic: number | undefined): string {
  if (moic === undefined || !isFinite(moic)) {
    return colors.neutral[50];
  }
  if (moic < 1) {
    return colors.critical[50];
  }
  if (moic < 1.5) {
    return colors.warning[50];
  }
  if (moic < 2.5) {
    return colors.success[60];
  }
  return colors.success[70];
}

/**
 * Colours for the restructure links, one per tie rather than one per direction.
 *
 * Both ends of a conversion carry the same link id, so both ends read in the same colour and the eye
 * can follow a restructure across the table — which is the point of the chips. Colouring by direction
 * would give the two halves of one conversion two colours and make them look unrelated.
 *
 * Three hue families are deliberately absent, because a restructure tint sits on the same row as the
 * app's own signals and must never be mistaken for one:
 *
 * - **indigo** (`primary`) is the selected row's wash and accent bar — `primary[60]` is the exact
 *   colour of that wash, so a restructure landing on it read as "this row is selected";
 * - **purple** (`secondary[50]`) is the grid theme's `accentColor`, what the app uses for interactive
 *   and selected states;
 * - **red** (`critical`) is a loss in the MOIC column, and a link is not a failure.
 *
 * What is left is a small palette of readable, well-separated hues. Two unrelated restructures can
 * share one, which costs nothing: only rows sharing a link are ever tinted at the same time.
 */
const RestructurePalette = [
  colors.tertiary[60],
  colors.warning[60],
  colors.success[70],
  colors.neutral[70],
  colors.tertiary[80],
];

export function getRestructureColor(groupId: string): string {
  const hash = [...groupId].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 100_000, 7);
  return RestructurePalette[hash % RestructurePalette.length];
}

export interface ILifecycleChip {
  label: string;
  /** Text colour; the background is the same hue at low opacity. */
  color: string;
}

/** `active` earns no chip — the design only marks lots that have left the ordinary state. */
export function getLifecycleChip(lifecycle: LifecycleState): ILifecycleChip | null {
  switch (lifecycle) {
    case 'converted':
      return { label: LifecycleLabels.converted, color: colors.neutral[60] };
    case 'partiallyRealized':
      return { label: LifecycleLabels.partiallyRealized, color: colors.warning[60] };
    case 'fullyRealized':
      return { label: LifecycleLabels.fullyRealized, color: colors.success[70] };
    case 'writtenOff':
      return { label: LifecycleLabels.writtenOff, color: colors.critical[50] };
    case 'active':
      return null;
  }
}

/**
 * A converted lot's capital was carried into the security it became rather than returned, so the
 * design writes "$9.5M carried" in its Invested cell instead of a bare amount.
 */
export function isCarriedInvestment(lot: IEquityLot | undefined): boolean {
  return lot?.lifecycle === 'converted';
}

/**
 * Security History lists the whole lifecycle, so unlike Overview it shows every lot — including the
 * realized, converted and written-off ones Overview filters out.
 */
export function selectHistoryLots(lots: IEquityLot[]): IEquityLot[] {
  return lots;
}

/**
 * The one-line summary the design writes, right-aligned, on each band row.
 *
 * Mirrors the prototype's band text: what is held, what went in, what it is worth, and what has come
 * back — or, for a band whose lots have all converted, just the capital that was carried onward.
 *
 *   "164,836 sh · $500k → $6.4M"
 *   "0 sh · $180k → $0 · +$1.1M realized"
 *   "$9.5M → converted"
 */
export function getBandSummary(lots: IEquityLot[]): string {
  const live = lots.filter((lot) => lot.lifecycle !== 'converted');
  const carried = sumBy(
    lots.filter((lot) => lot.lifecycle === 'converted'),
    (lot) => lot.invested
  );

  if (live.length === 0) {
    return `${FMT.format('currencyShort', carried)} → converted`;
  }

  const shares = sumBy(live, (lot) => lot.shares);
  const invested = sumBy(live, (lot) => lot.invested);
  const fmv = sumBy(live, (lot) => lot.fmv);
  const proceeds = sumBy(live, (lot) => lot.proceeds);

  const parts = [
    `${FMT.format('integer', shares)} sh · ${FMT.format('currencyShort', invested)} → ${FMT.format('currencyShort', fmv)}`,
  ];
  if (proceeds > 0) {
    parts.push(`+${FMT.format('currencyShort', proceeds)} realized`);
  }
  // A band mixing live and converted lots must account for the converted capital too. Without this the
  // header undercounts while the Invested column (aggFunc `sum`) and the pinned Total include it —
  // exactly the group-versus-total disagreement this card fixes elsewhere.
  if (carried > 0) {
    parts.push(`+${FMT.format('currencyShort', carried)} converted`);
  }

  return parts.join(' · ');
}

function sumBy(lots: IEquityLot[], pick: (lot: IEquityLot) => number): number {
  return lots.reduce((total, lot) => total + pick(lot), 0);
}
